import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/client';
import { API_CONFIG } from '../../config/api.config';
import {
  X,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Camera,
  Save,
  ArrowLeft
} from 'lucide-react';
import Avatar from '../common/Avatar';

export default function Profile() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [user, setUser] = useState(null);

  // 编辑状态
  const [isEditingAvatar, setIsEditingAvatar] = useState(false);
  const [isEditingNickname, setIsEditingNickname] = useState(false);
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isEditingPassword, setIsEditingPassword] = useState(false);

  // 表单数据
  const [editAvatar, setEditAvatar] = useState('');
  const [editNickname, setEditNickname] = useState('');
  const [editBio, setEditBio] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // 组件挂载时允许 body 滚动
  useEffect(() => {
    document.body.style.overflow = 'auto';

    return () => {
      // 组件卸载时恢复 overflow: hidden
      document.body.style.overflow = 'hidden';
    };
  }, []);

  // 获取用户信息
  const fetchUserInfo = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(API_CONFIG.ENDPOINTS.USER_INFO);
      if (response.code === 0) {
        setUser(response.data);
      } else {
        setError(response.message || '获取用户信息失败');
      }
    } catch (err) {
      console.error('获取用户信息失败:', err);
      setError('获取用户信息失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 获取用户数据
  useEffect(() => {
    fetchUserInfo();
  }, []);

  // 更新用户信息
  const updateUserInfo = async (updateData) => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const response = await apiClient.post(API_CONFIG.ENDPOINTS.USER_UPDATE, updateData);

      if (response.code === 0) {
        setSuccess('更新成功');
        setUser(response.data);

        // 更新 localStorage 中的用户信息
        localStorage.setItem('user', JSON.stringify(response.data));

        // 关闭所有编辑状态
        setIsEditingAvatar(false);
        setIsEditingNickname(false);
        setIsEditingBio(false);
        setIsEditingEmail(false);
        setIsEditingPassword(false);

        // 清空编辑表单
        setEditAvatar('');
        setEditNickname('');
        setEditBio('');
        setEditEmail('');
        setEditPassword('');

        // 3秒后隐藏成功提示
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.message || '更新失败');
      }
    } catch (err) {
      console.error('更新失败:', err);
      setError('更新失败，请稍后重试');
    } finally {
      setSaving(false);
    }
  };

  // 保存头像
  const handleSaveAvatar = () => {
    // 支持清空头像
    if (!editAvatar.trim()) {
      updateUserInfo({ avatar: '' });
      return;
    }

    if (!editAvatar.startsWith('http://') && !editAvatar.startsWith('https://')) {
      setError('头像链接必须以 http:// 或 https:// 开头');
      return;
    }

    updateUserInfo({ avatar: editAvatar });
  };

  // 保存昵称
  const handleSaveNickname = () => {
    if (!editNickname.trim()) {
      setIsEditingNickname(false);
      setEditNickname('');
      return;
    }

    if (editNickname.length > 100) {
      setError('昵称不能超过100个字符');
      return;
    }

    updateUserInfo({ nickname: editNickname });
  };

  // 保存签名
  const handleSaveBio = () => {
    if (editBio.length > 500) {
      setError('签名不能超过500个字符');
      return;
    }

    updateUserInfo({ bio: editBio });
  };

  // 保存邮箱
  const handleSaveEmail = () => {
    if (!editEmail.trim()) {
      setIsEditingEmail(false);
      setEditEmail('');
      return;
    }

    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(editEmail)) {
      setError('邮箱格式不正确');
      return;
    }

    updateUserInfo({ email: editEmail });
  };

  // 保存密码
  const handleSavePassword = () => {
    if (!editPassword.trim()) {
      setIsEditingPassword(false);
      setEditPassword('');
      return;
    }

    if (editPassword.length < 6) {
      setError('密码至少6个字符');
      return;
    }

    updateUserInfo({ password: editPassword });
  };

  // 取消编辑
  const handleCancelEdit = () => {
    setIsEditingAvatar(false);
    setIsEditingNickname(false);
    setIsEditingBio(false);
    setIsEditingEmail(false);
    setIsEditingPassword(false);
    setEditAvatar('');
    setEditNickname('');
    setEditBio('');
    setEditEmail('');
    setEditPassword('');
    setError('');
  };

  // 加载状态
  if (loading) {
    return (
      <div className="h-screen bg-[#f8f9fa] flex items-center justify-center">
        <div className="text-gray-500">加载中...</div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-[#f8f9fa] overflow-hidden flex flex-col">
      {/* 顶部导航 */}
      <header className="h-16 bg-[#1a1c1e] text-white flex items-center justify-between px-4 lg:px-8 shrink-0">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 hover:bg-white/10 p-2 rounded-[6px] transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
            <span className="text-sm">返回</span>
          </button>
        </div>
        <h1 className="text-lg font-semibold">我的主页</h1>
        <div className="w-20" />
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto p-4 lg:p-8">
        {/* 成功提示 */}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
            <p className="text-sm text-green-600">{success}</p>
          </div>
        )}

        {/* 错误提示 */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {user && (
          <div className="space-y-3">
            {/* 头像 */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">头像</h3>
                {!isEditingAvatar && (
                  <button
                    onClick={() => {
                      setIsEditingAvatar(true);
                      setEditAvatar(user.avatar || '');
                      setError('');
                    }}
                    className="text-sm text-green-600 hover:text-green-700"
                  >
                    {user.avatar ? '修改' : '添加'}
                  </button>
                )}
              </div>

              {isEditingAvatar ? (
                <div className="space-y-4">
                  <input
                    type="url"
                    value={editAvatar}
                    onChange={(e) => {
                      setEditAvatar(e.target.value);
                      setError('');
                    }}
                    placeholder="https://example.com/avatar.jpg"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    disabled={saving}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveAvatar}
                      disabled={saving}
                      className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save className="w-4 h-4" />
                      {saving ? '保存中...' : '保存'}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      disabled={saving}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      取消
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-4">
                  <Avatar src={user.avatar} name={user.nickname || user.username} size={80} radius={8} />
                  {!user.avatar && (
                    <p className="text-sm text-gray-500">暂无头像</p>
                  )}
                </div>
              )}
            </div>

            {/* 昵称 */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">昵称</h3>
                {!isEditingNickname && (
                  <button
                    onClick={() => {
                      setIsEditingNickname(true);
                      setEditNickname(user.nickname || '');
                      setError('');
                    }}
                    className="text-sm text-green-600 hover:text-green-700"
                  >
                    {user.nickname ? '修改' : '添加'}
                  </button>
                )}
              </div>

              {isEditingNickname ? (
                <div className="space-y-4">
                  <input
                    type="text"
                    value={editNickname}
                    onChange={(e) => {
                      setEditNickname(e.target.value);
                      setError('');
                    }}
                    placeholder="你的昵称"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    disabled={saving}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveNickname}
                      disabled={saving}
                      className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save className="w-4 h-4" />
                      {saving ? '保存中...' : '保存'}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      disabled={saving}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      取消
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  {user.nickname ? (
                    <p className="text-gray-900">{user.nickname}</p>
                  ) : (
                    <p className="text-sm text-gray-500">暂未设置昵称</p>
                  )}
                </div>
              )}
            </div>

            {/* 签名 */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">签名</h3>
                {!isEditingBio && (
                  <button
                    onClick={() => {
                      setIsEditingBio(true);
                      setEditBio(user.bio || '');
                      setError('');
                    }}
                    className="text-sm text-green-600 hover:text-green-700"
                  >
                    {user.bio ? '修改' : '添加'}
                  </button>
                )}
              </div>

              {isEditingBio ? (
                <div className="space-y-4">
                  <textarea
                    value={editBio}
                    onChange={(e) => {
                      setEditBio(e.target.value);
                      setError('');
                    }}
                    placeholder="介绍一下你自己..."
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                    disabled={saving}
                  />
                  <p className="text-xs text-gray-500 text-right">
                    {editBio.length}/500
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveBio}
                      disabled={saving}
                      className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save className="w-4 h-4" />
                      {saving ? '保存中...' : '保存'}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      disabled={saving}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      取消
                    </button>
                  </div>
                </div>
              ) : (
                <div className="min-h-[3rem]">
                  {user.bio ? (
                    <p className="text-gray-900 whitespace-pre-wrap">{user.bio}</p>
                  ) : (
                    <p className="text-sm text-gray-500">暂未设置签名</p>
                  )}
                </div>
              )}
            </div>

            {/* 邮箱 */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">邮箱</h3>
                {!isEditingEmail && (
                  <button
                    onClick={() => {
                      setIsEditingEmail(true);
                      setEditEmail(user.email || '');
                      setError('');
                    }}
                    className="text-sm text-green-600 hover:text-green-700"
                  >
                    修改
                  </button>
                )}
              </div>

              {isEditingEmail ? (
                <div className="space-y-4">
                  <input
                    type="email"
                    value={editEmail}
                    onChange={(e) => {
                      setEditEmail(e.target.value);
                      setError('');
                    }}
                    placeholder="your@email.com"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    disabled={saving}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveEmail}
                      disabled={saving}
                      className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save className="w-4 h-4" />
                      {saving ? '保存中...' : '保存'}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      disabled={saving}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      取消
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-gray-400" />
                  <p className="text-gray-900">{user.email}</p>
                </div>
              )}
            </div>

            {/* 密码 */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">密码</h3>
                {!isEditingPassword && (
                  <button
                    onClick={() => {
                      setIsEditingPassword(true);
                      setEditPassword('');
                      setError('');
                    }}
                    className="text-sm text-green-600 hover:text-green-700"
                  >
                    修改
                  </button>
                )}
              </div>

              {isEditingPassword ? (
                <div className="space-y-4">
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={editPassword}
                      onChange={(e) => {
                        setEditPassword(e.target.value);
                        setError('');
                      }}
                      placeholder="输入新密码（至少6个字符）"
                      className="w-full pl-10 pr-10 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border:border-transparent"
                      disabled={saving}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSavePassword}
                      disabled={saving}
                      className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save className="w-4 h-4" />
                      {saving ? '保存中...' : '保存'}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      disabled={saving}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      取消
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Lock className="w-5 h-5 text-gray-400" />
                  <p className="text-gray-900">••••••••</p>
                </div>
              )}
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
